import Constants from 'expo-constants';

import type {
  CardData,
  IEncryptionService,
  IGetWebviewPendingEncryption,
  OrbitalEncryptionResult,
  WorldpayEncryptionResult,
} from '..';
import { PaymentProcessor } from '..';

import { detectCardBrand } from './detect-card-brand-utils';

export interface EncryptionConfig {
  orbital?: {
    baseUrl: string;
    pieFormat: string;
    subscriberId: string;
    mode: 'eFPE' | 'FPE';
  };
  worldpay?: {
    eProtectUrl: string;
    paypageId: string;
  };
}

/** Match use-build-payment-processor: prefer buildRegion over legacy US-then-CA fallback. */
export function getRegionalEncryptionConfig(): EncryptionConfig {
  const eas = Constants.expoConfig?.extra?.eas;
  const buildRegion = eas?.buildRegion as 'US' | 'CA' | undefined;
  const regional = buildRegion ? eas?.[buildRegion] : undefined;

  return {
    orbital: regional?.orbital ?? eas?.US?.orbital ?? eas?.CA?.orbital,
    worldpay: regional?.worldpay ?? eas?.US?.worldpay ?? eas?.CA?.worldpay,
  };
}

/**
 * 1. Stores card data and creates a Promise when encrypt() is called
 * 2. The UI renders an invisible WebView that performs encryption
 * 3. WebView posts message back with encrypted data
 * 4. Service resolves the Promise with encrypted data
 */
export class WebViewEncryptionService implements IEncryptionService {
  private config: EncryptionConfig;
  private pendingEncryption: IGetWebviewPendingEncryption | null = null;

  constructor() {
    this.config = getRegionalEncryptionConfig();
  }

  /**
   * Timeout for WebView encryption so the user is never stuck indefinitely (e.g. script load failure, no postMessage).
   * Encryption is designed to run once per new-card flow (same as workspace/frontend) and should complete on the
   * first run no matter how long script load takes—we allow up to this duration before giving up.
   */
  private static readonly ENCRYPTION_TIMEOUT_MS = 120_000; // 2 minutes

  // Returns a Promise that will be resolved by the WebView when encryption completes (or rejected on timeout).
  // Injection happens once; we wait for scripts to be ready then inject the encryption call a single time.
  async encrypt(
    cardData: CardData,
    processor: PaymentProcessor,
    orderId?: string
  ): Promise<OrbitalEncryptionResult | WorldpayEncryptionResult> {
    // Validate config for the processor
    if (processor === PaymentProcessor.ORBITAL && !this.config.orbital) {
      throw new Error('Orbital encryption config not available');
    }
    if (processor === PaymentProcessor.WORLDPAY && !this.config.worldpay) {
      throw new Error('Worldpay encryption config not available');
    }

    const webViewSessionKey = `${orderId ?? 'encryption'}-${Date.now()}`;
    const activeConfig =
      processor === PaymentProcessor.ORBITAL ? this.config.orbital : this.config.worldpay;

    // Create a promise that will be resolved by the WebView (or rejected on timeout)
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        fn();
      };

      const timeoutId = setTimeout(() => {
        settle(() => {
          this.pendingEncryption = null;
          reject(
            new Error('Card encryption timed out. Please check your connection and try again.')
          );
        });
      }, WebViewEncryptionService.ENCRYPTION_TIMEOUT_MS);

      const onResult = (result: any) => {
        settle(() => {
          clearTimeout(timeoutId);
          this.pendingEncryption = null;
          if (processor === PaymentProcessor.ORBITAL) {
            resolve(this.transformOrbitalResult(result, cardData));
          } else {
            resolve(this.transformWorldpayResult(result));
          }
        });
      };

      const onError = (error: any) => {
        settle(() => {
          clearTimeout(timeoutId);
          this.pendingEncryption = null;
          reject(new Error(error.error || error.message || 'Encryption failed'));
        });
      };

      // Store pending encryption for WebView component to access
      this.pendingEncryption = {
        cardData,
        processor,
        orderId,
        webViewSessionKey,
        config: activeConfig,
        onResult,
        onError,
      };
    });
  }

  //  Get pending encryption (used by WebView component)
  getWebviewPendingEncryption(): IGetWebviewPendingEncryption | null {
    return this.pendingEncryption;
  }

  // Clear pending encryption (for cleanup)
  clearPendingEncryption(): void {
    this.pendingEncryption = null;
  }

  //  Transform Orbital encryption result from WebView format to our format
  private transformOrbitalResult(webViewResult: any, cardData: CardData): OrbitalEncryptionResult {
    return {
      encryptedCardNum: webViewResult.cryptCard,
      encryptedCvv: webViewResult.cryptCvv,
      pieFormat: webViewResult.pieFormat,
      pieMode: webViewResult.mode,
      piePhaseId: webViewResult.phase.toString(),
      pieIntegrityCheck: webViewResult.integrityCheck,
      pieSubscriberId: webViewResult.subscriberId,
      pieKeyID: webViewResult.keyId,
      cardBrand: detectCardBrand(cardData.cardNumber),
      bin: cardData.cardNumber.substring(0, 6),
    };
  }

  //  Transform Worldpay encryption result from WebView format to our format
  private transformWorldpayResult(webViewResult: any): WorldpayEncryptionResult {
    return {
      lowValueToken: webViewResult.lowValueToken,
      type: webViewResult.type,
      bin: webViewResult.bin,
      last4: webViewResult.last4,
      expiryMonth: webViewResult.expiryMonth,
      expiryYear: webViewResult.expiryYear,
    };
  }
}
